import { Table, TableProps } from 'antd';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { routes } from 'config/routes';
import { getSortLabel, TABLE_SORT_DIRECTIONS_CONFIG } from 'config/tableSort';
import { FormListEntity } from 'entities/FormList';
import { FormStatus } from 'entities/common';
import { FormListPageStore } from 'stores/local';

import s from './FormListTable.module.scss';
import { useColumns } from './columns';

type FormListTableProps = {
  store: FormListPageStore;
};

const getEntityKey = (entity: FormListEntity) => entity.id;

const FormListTable: React.FC<FormListTableProps> = ({ store }) => {
  const navigate = useNavigate();

  const onRow = React.useCallback(
    (row: FormListEntity) => {
      return {
        className: cn(row.status === FormStatus.PUBLISHED && s.table__row),
        onClick: () => {
          if (row.status === FormStatus.PUBLISHED) {
            navigate(routes.form.formReview.createPath(row.id), {
              replace: true,
            });
          }
        },
      };
    },
    [navigate]
  );

  const handleParamsChange = React.useCallback(
    async (
      ...[pagination, filter, sort]: Parameters<
        NonNullable<TableProps<any>['onChange']>
      >
    ) => {
      await store.handleTableParamsChange({
        sort,
        filter,
        pagination,
      });
    },
    [store]
  );

  const columns = useColumns(store);

  return (
    <Table
      className={s.table}
      pagination={false}
      dataSource={store.formList}
      loading={store.loading}
      columns={columns}
      sortDirections={TABLE_SORT_DIRECTIONS_CONFIG}
      showSorterTooltip={{
        title: getSortLabel(store.sortOrder),
      }}
      onChange={handleParamsChange}
      rowKey={getEntityKey}
      onRow={onRow}
    />
  );
};

export default observer(FormListTable);
